/**
 * The swish of a saw blade or a rotor's arm the local player slips past (a near miss, as the
 * blades come close: they are what moves and buzzes in a level): once per blade per attempt.
 * Reads the level's World where the physics has its blades at the run's tick.
 */
import { MAX_ROUND_CIRCLES, MODE_BOXES, type PlayerState, type World } from '@cube-rush/shared';

/** A blade this close (blocks, from its deadly circle to the player's box) is a near miss. */
const NEAR = 0.6;

export class BladeWatch {
  /** Blades (World.rounds indices) already heard in this attempt. */
  private readonly heard = new Set<number>();
  private attempt = -1;
  private readonly circles = new Float64Array(3 * MAX_ROUND_CIRCLES);

  /**
   * Whether the player in state `s` (alive, in a level whose World is `world`) has just come
   * within NEAR of a blade not heard yet in this attempt (`attempt`: a key of the level and the
   * attempt; a new one forgets them).
   */
  check(world: World, s: Readonly<PlayerState>, attempt: number): boolean {
    if (attempt !== this.attempt) {
      this.attempt = attempt;
      this.heard.clear();
    }
    if (world.rounds.length === 0 || !s.alive) return false;
    const half = MODE_BOXES[s.mode].hazard.w / 2;
    const reach = half + NEAR;
    const index = world.roundIndex;
    const n = index.query(s.x - reach, s.x + reach);
    for (let i = 0; i < n; i++) {
      const id = index.found[i]!;
      if (this.heard.has(id) || world.roundFrom[id]! > s.x + reach) continue;
      if (world.roundTo[id]! < s.x - reach) continue;
      const count = world.roundCircles(id, s.tick, this.circles);
      for (let c = 0; c < count; c++) {
        const cx = this.circles[3 * c]!;
        const cy = this.circles[3 * c + 1]!;
        const r = this.circles[3 * c + 2]! + NEAR;
        const dx = Math.max(0, Math.abs(cx - s.x) - half);
        const dy = Math.max(0, Math.abs(cy - s.y) - half);
        if (dx * dx + dy * dy < r * r) {
          this.heard.add(id);
          return true;
        }
      }
    }
    return false;
  }
}
